import {
  SUPPORT_ATTACHMENT_EXTENSIONS,
  SUPPORT_ATTACHMENT_MAX_BYTES,
  SUPPORT_ATTACHMENT_TYPES,
  SUPPORT_BOT_NAME,
  SUPPORT_OPEN_STATUSES,
  SUPPORT_SECTION_LIMIT,
  SUPPORT_SENDER,
  SUPPORT_STATUS,
  SUPPORT_SUBJECT_MAX,
} from '@/constants/support'

// Pure shaping of the support service's payloads: every computation the Chat tab needs that
// doesn't touch a reactive ref lives here, so the components stay glue.

/**
 * How a conversation row dates itself, in the design's three registers: minutes and hours
 * stay relative, the day before is named, and anything older is a date.
 *
 * `timeAgo` in helpers/times.js is NOT reusable here: it collapses everything between one
 * hour and a day to the word "recently", and never says "Yesterday" — the two labels the
 * rows lean on most.
 */
export const supportTimeLabel = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`

  const hours = Math.floor(minutes / 60)
  // Measured in calendar days, not 24-hour blocks: 11pm and 1am are "yesterday" to a reader
  // even though barely two hours separate them.
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000)

  if (days === 0) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  if (days === 1) return 'Yesterday'

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The clock under a bubble: "10:42 AM".
export const supportMessageTime = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// A resolved or closed ticket dates itself by when it ENDED, which is the fact the dealer
// is scanning for; one still open dates itself by its last activity.
export const ticketTimeLabel = (conversation) => {
  const status = conversation?.status
  const stamp = conversation?.lastMessageAt ?? conversation?.updatedAt ?? conversation?.createdAt

  if (status === SUPPORT_STATUS.RESOLVED || status === SUPPORT_STATUS.CLOSED) {
    return `Resolved ${supportTimeLabel(stamp)}`
  }

  return `Updated ${supportTimeLabel(stamp)}`
}

export const isOpenConversation = (conversation) => SUPPORT_OPEN_STATUSES.includes(conversation?.status)

// Two words for four statuses: the dealer only needs to know whether anyone is still on it.
export const statusLabel = (status) => (SUPPORT_OPEN_STATUSES.includes(status) ? 'Open' : 'Closed')

// AppBadge variants, matching Figma's green-50/green-500 and gray-100/gray-500 pills.
export const statusVariant = (status) => (SUPPORT_OPEN_STATUSES.includes(status) ? 'green' : 'gray')

// The agent-facing reference, e.g. "Ticket #31437". The API sends it WITH the "Ticket #"
// prefix already on it, so nothing here adds one.
export const ticketReference = (conversation) => conversation?.ticketNumber?.trim() || ''

// The digits of that reference, which the thread's URL names it by ("Ticket #31437" → "31437"); '' without one.
export const ticketDigits = (conversation) => ticketReference(conversation).match(/\d+/)?.[0] ?? ''

export const conversationSubject = (conversation) => conversation?.subject?.trim() || 'Support request'

/** The row's heading: "Ticket #31437 • How can I get started with Magic Scan?". */
export const ticketTitle = (conversation) => {
  const reference = ticketReference(conversation)
  const subject = conversationSubject(conversation)

  return reference ? `${reference} • ${subject}` : subject
}

// Who said it, as the thread labels each turn; the handoff note is the assistant's card whoever the service posts it as.
export const senderLabel = (message) => {
  if (isOwnMessage(message)) return 'You'
  if (isHandoffNote(message)) return SUPPORT_BOT_NAME
  return message?.senderName?.trim() || SUPPORT_BOT_NAME
}

// The AI asks follow-ups as a closed set of choices; without rendering these the dealer has
// no way to answer and the conversation stalls on screen.
export const messageChoices = (message) => (
  message?.structuredPayload?.type === 'choices' && Array.isArray(message.structuredPayload.options)
    ? message.structuredPayload.options
    : []
)

// Split one endpoint into the design's two sections. Newest first in both.
export const splitConversations = (conversations = []) => {
  const byRecency = [...conversations].sort((a, b) => (
    new Date(b.lastMessageAt ?? b.updatedAt ?? b.createdAt ?? 0) -
    new Date(a.lastMessageAt ?? a.updatedAt ?? a.createdAt ?? 0)
  ))

  return {
    tickets: byRecency.filter(isOpenConversation),
    previous: byRecency.filter((conversation) => !isOpenConversation(conversation)),
  }
}

// What a section shows before "View all".
export const sectionSlice = (list = []) => list.slice(0, SUPPORT_SECTION_LIMIT)

// Case- and accent-blind, for the list pages' filter fields: typing "ebay" finds "eBay".
const fold = (text) => String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

// Every word of the query is somewhere in the text, in any order; a word is also tried against the text with its spaces
// removed, so "how setup" finds "How to Set Up Title Templates".
export const matchesQuery = (text, query) => {
  const words = fold(query).split(/\s+/).filter(Boolean)
  if (!words.length) return true

  const haystack = fold(text)
  const joined = haystack.replace(/\s+/g, '')

  return words.every((word) => haystack.includes(word) || joined.includes(word))
}

// A conversation matches on what its row shows: the ticket reference and the subject.
export const filterConversations = (conversations = [], query) => conversations.filter((conversation) => matchesQuery(ticketTitle(conversation), query))

export const filterQuestions = (questions = [], query) => questions.filter((question) => matchesQuery(question, query))

// Anything that isn't the dealer's own message is drawn as a reply — an unknown sender
// should read as the assistant, never be mistaken for something the dealer said.
export const isOwnMessage = (message) => message?.senderType === SUPPORT_SENDER.CUSTOMER

// The thread is a person's now, as its record says (the support widget reads the same two fields).
export const isEscalatedConversation = (conversation) => !!conversation && (conversation.aiAutoRespond === 'false' || !!conversation.handoffAt)

// The line the service posts when a thread is handed to a person; its widget rewrites it to a note of its own, and so does the thread.
const HANDOFF_LINE = /^A support agent will join you(?: in about[^.]*| as soon as one is free(?: \(\d+ customers ahead of you\))?)\.\s*$/i

// The note that the thread was handed to a person: the service's own line, or the one the panel adds when none came.
export const isHandoffNote = (message) => message?.handoff === true
  || (message?.senderType === SUPPORT_SENDER.SYSTEM && HANDOFF_LINE.test(String(message.content ?? '').trim()))

// A reply's words without the AI's own markers (`~~confidence: low`, `~~follow-up: …`), which its widget strips too.
export const replyText = (message) => String(message?.content ?? '')
  .split('\n')
  .filter((line) => !/^~~(follow-up|confidence):/i.test(line))
  .join('\n')
  .trimEnd()

// The last line is a reply from the server: the one the thread was waiting for.
export const isAnswered = (messages = []) => {
  const last = messages.at(-1)
  return !!last && !isOwnMessage(last) && !last.local
}

// A conversation is opened with the dealer's own words, so the subject is that line kept to
// a length an agent can scan in a list.
export const supportSubject = (text) => {
  const line = (text ?? '').trim().replace(/\s+/g, ' ')
  if (line.length <= SUPPORT_SUBJECT_MAX) return line
  return `${line.slice(0, SUPPORT_SUBJECT_MAX - 1).trimEnd()}…`
}

/**
 * Whether the dealer is asking what changed, the one question answered from our own content
 * instead of by the support AI.
 *
 * Deliberately narrow: it matches a short question that is ABOUT the release notes and
 * nothing else. "What's new?" is answered locally; "what's new with my eBay sync" is a
 * support question and must reach support, so the length cap keeps the shortcut from
 * swallowing real requests.
 */
export const isWhatsNewAsk = (text) => {
  const line = (text ?? '').trim().toLowerCase().replace(/[?!.]+$/, '')
  if (line.length > 40) return false

  return /^(what'?s|whats)\s+new$/.test(line)
    || /^(what'?s|whats)\s+new\s+(in|with|on)\s+cdp$/.test(line)
    || /^(release|changelog|what'?s changed|recent updates|latest updates)$/.test(line)
    || /^(show|see|view)\s+(me\s+)?(the\s+)?(release notes|changelog|what'?s new)$/.test(line)
}

/**
 * The release-notes card the assistant answers "What's new?" with, built from the Strapi
 * changelog the Help tab already loads — the support service has no changelog of its own,
 * and ours is the one that is actually current.
 */
export const releaseNotesFromChangelog = (posts = [], limit = SUPPORT_SECTION_LIMIT) => posts
  .slice(0, limit)
  .map((post) => {
    const attributes = post.attributes ?? {}
    const [section] = attributes.section ?? []
    const date = attributes.date ? new Date(attributes.date) : null

    return {
      id: post.id,
      // "August 2026" — the badge; a post without a date keeps the row rather than losing it.
      period: date && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : '',
      version: attributes.title ?? '',
      summary: section?.description ?? section?.title ?? '',
    }
  })
  .filter((note) => note.summary)

/**
 * The content type to upload a file as: its own, corrected for the `image/jpg` some browsers
 * report, or derived from the extension when the browser gives none.
 */
export const attachmentContentType = (file) => {
  const declared = (file?.type || '').toLowerCase()
  if (declared === 'image/jpg') return 'image/jpeg'
  if (SUPPORT_ATTACHMENT_TYPES.includes(declared)) return declared

  const extension = (file?.name || '').split('.').pop()?.toLowerCase() ?? ''
  return SUPPORT_ATTACHMENT_EXTENSIONS[extension] ?? declared
}

/** Why the support service would refuse this file, or null when it would take it. */
export const attachmentRejection = (file) => {
  if (!SUPPORT_ATTACHMENT_TYPES.includes(attachmentContentType(file))) return "That file type isn't supported"
  if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) return 'That file is over the 10MB limit'
  return null
}

export const isImageAttachment = (attachment) => String(attachment?.contentType ?? attachment?.content_type ?? '').startsWith('image/')

// "2.4 MB" — the size under a pending file's name.
export const formatAttachmentSize = (bytes) => {
  const size = Number(bytes) || 0
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}
